/**
 * Finn erstatter (issue #147 F1, candidate addendum AI-01 to AI-14): the people who could take one
 * shift, sorted into what the manager has to know before choosing. The verdicts are advice.ts
 * ranked(), which is cover.ts and core's own rules; this adds what the availability rows say about
 * the shift's hours, and nothing the app does not already check. No competence, no score, no "best".
 * Pure; tested in coverFlow.test.ts.
 */
import type { ApiAvailability } from '@/api/availability';
import type { ApiShift } from '@/api/data';
import { oslo, osloISO } from '@/rules';
import { addDays, dow } from '@timefly/core';
import type { Option } from './advice';
import { dateOf } from './model';

/** What the person told the company about the shift's hours. Nothing recorded is not "available". */
export type Availability =
  | { kind: 'reported'; from: string; to: string; pending: boolean }
  | { kind: 'partly'; from: string; to: string; pending: boolean }
  | { kind: 'unavail' }
  | { kind: 'unknown' };

/**
 * The tiers the panel shows, in this order. `eligible`: nothing in the way and availability
 * reported for the hours. `missing`: nothing in the way the plan knows of, but no availability on
 * record. `review`: a rule or the agreed week would be touched; the manager decides. `blocked`:
 * absence, unavailable or another shift then; no way to choose it.
 */
export type Tier = 'eligible' | 'missing' | 'review' | 'blocked';
export interface CoverCandidate extends Option {
  tier: Tier;
  availability: Availability;
  /** less than the 11 h rest: never offered as a quick choice (review of #147, F1) */
  restBreach: boolean;
}

const ms = (date: string, time: string) => Date.parse(osloISO(date, time));
type Span = { from: number; to: number; pending: boolean };

/**
 * One availability row as real date-time intervals near `day`: a dated row is its own instants, a
 * recurring one an interval on each matching date (one that ends at or before it starts runs past
 * midnight). A row to 23:59 runs to the next midnight, so two days join without a gap.
 */
function spansOf(r: ApiAvailability, days: string[]): Span[] {
  const st = oslo(r.start_date_time);
  const en = oslo(r.end_date_time);
  if (!st || !en) return [];
  const pending = r.approval_status === 'pending';
  const end = (date: string, time: string) =>
    time >= '23:59' ? ms(addDays(date, 1), '00:00') : ms(date, time);
  if (!r.is_recurring) return [{ from: ms(st.date, st.time), to: end(en.date, en.time), pending }];
  const until = r.recurrence_end_date ? (oslo(r.recurrence_end_date)?.date ?? st.date) : '9999-12-31';
  return days
    .filter(
      (d) =>
        d >= st.date && d <= until && (r.recurrence_pattern === 'weekly' ? dow(d) === dow(st.date) : true),
    )
    .map((d) => ({
      from: ms(d, st.time),
      to: en.time > st.time ? end(d, en.time) : end(addDays(d, 1), en.time),
      pending,
    }));
}

/**
 * What the availability rows say about the whole shift, a night's next morning included (review
 * R2): unavailable if any unavailable row touches it; reported if the available rows, joined where
 * they meet, cover it from start to end; partly if they cover some of it; else unknown.
 */
export function availabilityFor(rows: ApiAvailability[], userId: number, s: ApiShift): Availability {
  const S = Date.parse(s.start_time);
  const E = Date.parse(s.end_time);
  const d = dateOf(s);
  const days = [addDays(d, -1), d, addDays(d, 1)];
  const mine = rows.filter((r) => r.user_id === userId && r.approval_status !== 'rejected');
  const touches = (x: Span) => x.from < E && x.to > S;
  if (mine.some((r) => !r.is_available && spansOf(r, days).some(touches))) return { kind: 'unavail' };
  const spans = mine
    .filter((r) => r.is_available)
    .flatMap((r) => spansOf(r, days))
    .filter(touches)
    .sort((a, b) => a.from - b.from);
  if (!spans.length) return { kind: 'unknown' };
  // join the intervals that meet or overlap
  const joined: Span[] = [];
  for (const x of spans) {
    const last = joined.at(-1);
    if (last && x.from <= last.to) {
      last.to = Math.max(last.to, x.to);
      last.pending ||= x.pending;
    } else joined.push({ ...x });
  }
  const whole = joined.find((x) => x.from <= S && x.to >= E);
  const show = whole ?? (joined[0] as Span);
  const at = (t: number) => oslo(new Date(t).toISOString())?.time ?? '';
  // the clock times of the interval that decides it; a run to midnight reads as 23:59, as entered
  const to = at(show.to) === '00:00' ? '23:59' : at(show.to);
  return { kind: whole ? 'reported' : 'partly', from: at(show.from), to, pending: show.pending };
}

const RANK: Record<Tier, number> = { eligible: 0, missing: 1, review: 2, blocked: 3 };

/** ranked()'s list, each person with their tier and availability; ranked()'s order kept inside a tier. */
export function tiersOf(options: Option[], avail: ApiAvailability[], s: ApiShift): CoverCandidate[] {
  return options
    .map((o, i) => {
      const availability = availabilityFor(avail, o.userId, s);
      const restBreach = o.why.some((w) => w.endsWith('t hvile'));
      // a breach the change would make is a block, not a warning: the server judges under 11 h rest
      // a breach (AML §10-8(1), severity danger) and the app has no exception to record (review R3)
      const breach = restBreach || o.adds.some((e) => e.sev === 'd');
      const tier: Tier =
        o.kind === 'blocked' || breach
          ? 'blocked'
          : o.kind === 'review'
            ? 'review'
            : availability.kind === 'reported'
              ? 'eligible'
              : 'missing';
      return { c: { ...o, tier, availability, restBreach }, i };
    })
    .sort((a, b) => RANK[a.c.tier] - RANK[b.c.tier] || a.i - b.i)
    .map((x) => x.c);
}

/** What the preview was made from: if the shift is not this any more, the preview is stale (UC13). */
export interface Seen {
  id: number;
  user_id: number | null;
  start_time: string;
  end_time: string;
  published: boolean;
  updated_at?: string | undefined;
}
export const seenOf = (s: ApiShift): Seen => ({
  id: s.id,
  user_id: s.user_id,
  start_time: s.start_time,
  end_time: s.end_time,
  published: s.published,
  updated_at: (s as { updated_at?: string }).updated_at,
});
export function changedSince(seen: Seen, now: ApiShift | undefined): boolean {
  if (!now) return true;
  const n = seenOf(now);
  return (
    n.user_id !== seen.user_id ||
    Date.parse(n.start_time) !== Date.parse(seen.start_time) ||
    Date.parse(n.end_time) !== Date.parse(seen.end_time) ||
    n.published !== seen.published ||
    (!!n.updated_at && !!seen.updated_at && n.updated_at !== seen.updated_at)
  );
}

/** The verdict on one person, as the manager saw it: a different one after a fresh read is stale. */
export const verdictKey = (
  c: Pick<CoverCandidate, 'tier' | 'why' | 'adds' | 'availability' | 'before' | 'after' | 'norm'> | undefined,
) =>
  // everything the preview showed: the tier and reasons, each rule with its measured values, the
  // availability with its times and approval, and the hours (review R1)
  c
    ? JSON.stringify([
        c.tier,
        c.why,
        c.adds.map((e) => [e.code, e.sev, e.args ?? null]),
        c.availability,
        c.before,
        c.after,
        c.norm,
      ])
    : 'gone';
